import { useAtom } from 'jotai';
import { useEffect, useCallback, useRef } from 'react';
import { feedsAtom, loadingAtom, errorAtom } from '../utils/feedState';
import { FeedService } from '../utils/feedService';
import { Storage } from '../utils/storage';
import { defaultFeeds } from '../utils/defaultFeeds';

export function useFeeds() {
  const [feeds, setFeeds] = useAtom(feedsAtom);
  const [loading, setLoading] = useAtom(loadingAtom);
  const [error, setError] = useAtom(errorAtom);
  const initialLoadDone = useRef(false);
  const storageInitialized = useRef(false);

  const initializeStorage = useCallback(async () => {
    if (storageInitialized.current) return;
    
    try {
      await Storage.init();
      storageInitialized.current = true;
    } catch (error) {
      console.error('Failed to initialize storage:', error);
      setError('Failed to initialize storage. Using default feeds.');
      setFeeds(defaultFeeds);
    }
  }, [setError, setFeeds]);

  const refreshFeeds = useCallback(async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await initializeStorage();
      const updatedFeeds = await FeedService.updateFeeds();
      
      if (updatedFeeds && updatedFeeds.length > 0) {
        setFeeds(updatedFeeds);
      } else {
        setFeeds(defaultFeeds);
        setError('No feeds available. Using default content.');
      }
    } catch (err) {
      console.error('Feed refresh error:', err);
      setFeeds(defaultFeeds);
      setError('Failed to refresh feeds. Using default content.');
    } finally {
      setLoading(false);
    }
  }, [setFeeds, setError, setLoading, loading, initializeStorage]);

  useEffect(() => {
    let mounted = true;

    const loadFeeds = async () => {
      if (initialLoadDone.current) return;
      
      try {
        await initializeStorage();

        // Load cached feeds first
        const cachedFeeds = await Storage.loadFeeds();
        if (mounted && cachedFeeds.length > 0) {
          setFeeds(cachedFeeds);
          setLoading(false);
        } else {
          // If no cached feeds, use defaults and trigger a refresh
          setFeeds(defaultFeeds);
          if (mounted) {
            await refreshFeeds();
          }
        }
        
        initialLoadDone.current = true;
      } catch (err) {
        console.error('Feed loading error:', err);
        if (mounted) {
          setFeeds(defaultFeeds);
          setError('Failed to load feeds. Using default content.');
          setLoading(false);
        }
      }
    };

    loadFeeds();

    return () => {
      mounted = false;
    };
  }, [setFeeds, setLoading, setError, refreshFeeds, initializeStorage]);

  return { 
    feeds: feeds.length > 0 ? feeds : defaultFeeds,
    loading, 
    error, 
    refreshFeeds 
  };
}